import {Injectable, isDevMode} from '@angular/core';
import {of} from 'rxjs';
import {catchError, take} from 'rxjs/operators';
import {AnalyticsService} from '../analytics/analytics.service';
import {
  TrackingEventName,
  TrackingEventPayload,
  TrackingPayloadMap,
} from '../../entities/tracking/tracking-event.entity';
import {TrackingApiService} from './tracking-api.service';
import {TrackingIdService} from './tracking-id.service';

const IDEMPOTENCY_ALLOWED_EVENTS = new Set<TrackingEventName>([
  TrackingEventName.CHECKOUT_STARTED,
  TrackingEventName.CHECKOUT_SUCCEEDED,
  TrackingEventName.CHECKOUT_CANCELLED,
]);

interface TrackOptions {
  flow?: string | null;
  trackingId?: string | null;
  attachTrackingId?: boolean;
  occurredAt?: Date | string | null;
  idempotencyKey?: string | null;
  allowUnsafeIdempotencyKey?: boolean;
  gaEventName?: string;
  sendToGa?: boolean;
  sendToApi?: boolean;
  rememberFlow?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TrackingService {
  private readonly flowStorageKey = 'sf_tracking_flow';

  public constructor(
    private readonly analytics: AnalyticsService,
    private readonly trackingApi: TrackingApiService,
    private readonly trackingId: TrackingIdService,
  ) {
  }

  /**
   * Sends the event both to GA and to the backend tracking endpoint.
   */
  public track<K extends TrackingEventName>(
    name: K,
    payload: TrackingPayloadMap[K],
    options: TrackOptions = {},
  ): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (options.sendToGa !== false) {
      const gaEventName = options.gaEventName ?? name.toLowerCase();
      this.analytics.event(gaEventName, payload);
    }

    if (options.sendToApi === false) {
      return;
    }

    const body: TrackingEventPayload = { name };
    if (options.flow) {
      body.flow = options.flow;
      if (options.rememberFlow !== false) {
        this.rememberFlow(options.flow);
      }
    }
    const occurredAt = this.computeOccurredAt(options.occurredAt);
    const hasTrackingIdOption = Object.prototype.hasOwnProperty.call(options, 'trackingId');
    if (hasTrackingIdOption) {
      body.trackingId = options.trackingId ?? null;
    } else if (options.attachTrackingId !== false) {
      body.trackingId = this.trackingId.getOrCreateTrackingId();
    }
    const trackingId = typeof body.trackingId === 'string' && body.trackingId.length > 0
      ? body.trackingId
      : null;
    if (occurredAt) {
      body.occurredAt = occurredAt;
    }
    const idempotencyKey = options.idempotencyKey;
    if (typeof idempotencyKey === 'string' && idempotencyKey.length > 0) {
      if (IDEMPOTENCY_ALLOWED_EVENTS.has(name)) {
        body.idempotencyKey = this.scopeIdempotencyKey(idempotencyKey, trackingId);
      } else if (options.allowUnsafeIdempotencyKey) {
        body.idempotencyKey = this.scopeIdempotencyKey(idempotencyKey, trackingId);
        this.warnUnsafeIdempotencyKey(name, idempotencyKey, true);
      } else {
        this.warnUnsafeIdempotencyKey(name, idempotencyKey, false);
      }
    }

    body.payload = payload;

    this.trackingApi.trackEvent(body)
      .pipe(
        take(1),
        catchError((error) => {
          console.error('Failed to persist tracking event', { name, error });
          return of(null);
        })
      )
      .subscribe();
  }

  private computeOccurredAt(occurredAt?: Date | string | null): string | null {
    if (occurredAt instanceof Date) {
      return occurredAt.toISOString();
    }
    if (typeof occurredAt === 'string') {
      return occurredAt;
    }
    return null;
  }

  public getLastFlow(): string | null {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return null;
    try {
      return localStorage.getItem(this.flowStorageKey);
    } catch {
      return null;
    }
  }

  private rememberFlow(flow: string): void {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(this.flowStorageKey, flow);
    } catch {
      // ignore storage errors
    }
  }

  private warnUnsafeIdempotencyKey(
    name: TrackingEventName,
    key: string,
    allowedBypass: boolean,
  ): void {
    if (!isDevMode()) return;
    if (allowedBypass) {
      console.warn(`Bypassed idempotencyKey allowlist for ${name}: ${key}`);
      return;
    }
    console.warn(`Ignored idempotencyKey for ${name}: ${key}`);
  }

  private scopeIdempotencyKey(key: string, trackingId: string | null): string {
    if (!trackingId) {
      return key;
    }

    const scoped = `t:${trackingId}:${key}`;
    if (scoped.length <= 191) {
      return scoped;
    }

    return `h:${this.hashString(scoped)}`;
  }

  private hashString(input: string): string {
    let hash = 5381;
    for (let i = 0; i < input.length; i += 1) {
      hash = ((hash << 5) + hash) + input.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash).toString(16);
  }
}
